import { useLayoutEffect } from 'react';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { extractFinancingIdentifiersFromResponse } from '../utils/extractFinancingIdentifiersFromResponse';
import { trackEvent } from '../utils/fullstory-tracker';

interface RequestMetadata {
    baseProperties: Record<string, any>;
}

const REQUEST_METADATA_KEY = '__fullstory_metadata__';

/**
 * Hook to set up axios interceptors that send FullStory custom events for API calls
 * Tracks API calls after they complete, including status code, success/failure,
 * and financingId / loanId / externalLoanId from the response body when present
 *
 * Only tracks calls to financing and prequals APIs (excludes payments API).
 * Filters out GET requests to settings/accounts endpoints to reduce FullStory API usage.
 * This hook assumes FullStory is already installed in the host application.
 * It will gracefully handle cases where FullStory is not available.
 *
 * @example
 * useAxiosFullStoryInterceptor();
 */
export const useAxiosFullStoryInterceptor = () => {
    useLayoutEffect(() => {
        // Request interceptor - mark requests that should be tracked
        const requestInterceptorId = axios.interceptors.request.use(
            (requestConfig: InternalAxiosRequestConfig) => {
                // Only track financing and prequals API calls (excluding payments)
                const baseURL = requestConfig.baseURL ?? '';
                const isFinancingAPI =
                    baseURL.includes('/api/financing') &&
                    !baseURL.includes('/api/financing/payments');

                const isPrequalsAPI = baseURL.includes('/api/prequals');

                if (!isFinancingAPI && !isPrequalsAPI) {
                    return requestConfig;
                }

                // Filter out settings GET requests (not critical business actions)
                const url = requestConfig.url ?? '';
                const isSettingsCall =
                    requestConfig.method?.toUpperCase() === 'GET' &&
                    (url.includes('/settings') || url.includes('/accounts'));

                if (isSettingsCall) {
                    return requestConfig;
                }

                // Build base properties upfront
                const baseProperties: Record<string, any> = {
                    method: requestConfig.method?.toUpperCase(),
                    url: requestConfig.url,
                    sessionId: requestConfig.headers?.['X-ST-SessionId'],
                    correlationId: requestConfig.headers?.['X-ST-CorrelationId'],
                    providerName: requestConfig.headers?.['X-ST-FIN-Provider'],
                    source: requestConfig.headers?.['X-ST-FIN-Origin'],
                };

                // Store metadata for response interceptor
                (requestConfig as any)[REQUEST_METADATA_KEY] = {
                    baseProperties,
                } as RequestMetadata;

                return requestConfig;
            },
        );

        // Response interceptor - track successful API calls
        const responseInterceptorId = axios.interceptors.response.use((response: AxiosResponse) => {
            const metadata = (response.config as any)[REQUEST_METADATA_KEY] as
                RequestMetadata | undefined;

            if (metadata?.baseProperties) {
                const statusCode = response.status;
                const loanIdentifiers = extractFinancingIdentifiersFromResponse(response.data);

                // Mix in response-specific properties
                const eventProperties = {
                    ...metadata.baseProperties,
                    ...loanIdentifiers,
                    statusCode,
                    success: statusCode >= 200 && statusCode < 300,
                };

                trackEvent('FIN-API-Call', eventProperties);
            }

            return response;
        });

        // Cleanup: eject interceptors when component unmounts
        return () => {
            axios.interceptors.request.eject(requestInterceptorId);
            axios.interceptors.response.eject(responseInterceptorId);
        };
    }, []);
};
